import * as React from 'react'
import i18next from 'i18next'
import { Spinner } from '../Spinner'
import { SelectedChips } from '../FiltersDrawer/SelectedChips.component'
import { useTagsInput } from './TagsInput.hook'

interface TagsInputProps {
  workspaceId: string
  initialTags?: Array<string>
  onChange?: (tags: Array<string>) => void
}

export const TagsInput: React.FC<TagsInputProps> = ({ workspaceId, initialTags, onChange }) => {
  const { tags, query, setQuery, suggestions, loading, addTag, removeTag } = useTagsInput({
    workspaceId,
    initialTags,
    onChange,
  })
  const [isOpen, setIsOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag(query)
    } else if (e.key === 'Backspace' && !query && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const showDropdown = isOpen && (loading || suggestions.length > 0)

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Dodaj tag..."
          autoComplete="off"
          className="w-full rounded-md border-0 py-2.5 pl-3 pr-9 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
        />
        {loading && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>

      {showDropdown && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
          {loading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-gray-500">{i18next.t('loading.searching')}</li>
          )}
          {suggestions.map((tag) => (
            <li
              key={tag}
              className="cursor-pointer select-none px-3 py-2 text-gray-900 hover:bg-indigo-600 hover:text-white"
              onClick={() => {
                addTag(tag)
                setIsOpen(false)
              }}
            >
              {tag}
            </li>
          ))}
        </ul>
      )}

      <SelectedChips items={tags.map((tag) => ({ id: tag, label: tag }))} onRemove={removeTag} />
    </div>
  )
}
